<template>
  <div class="container-fluid">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div>
        <h5 class="mb-0 font-weight-bold">User Setting</h5>
        <p class="text-secondary text-sm mb-0">User list &amp; registration requests</p>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card mb-4 user-shell-card">

          <!-- Modal: Create / Edit User -->
          <div v-if="showModal" class="modal-backdrop" style="position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:1040;"></div>
          <div v-if="showModal" class="modal d-block user-modal" tabindex="-1" style="z-index:1050;">
            <div class="modal-dialog modal-dialog-centered user-modal-dialog">
              <div
                ref="userModalContent"
                class="modal-content user-modal-content"
                :class="{ 'user-modal-content--locked': lockedModalHeight }"
                :style="userModalContentStyle"
              >
                <div class="modal-header flex-shrink-0">
                  <h6 class="modal-title">{{ modalTitle }}</h6>
                </div>
                <div class="modal-body user-modal-body" :class="{ 'user-modal-body--locked': lockedModalHeight }">
                  <div class="row">
                    <div class="col-6 mb-3">
                      <label class="form-label text-xs">First Name <span class="text-danger">*</span></label>
                      <input type="text" class="form-control form-control-sm" v-model="form.firstName" placeholder="First name" />
                    </div>
                    <div class="col-6 mb-3">
                      <label class="form-label text-xs">Last Name <span class="text-danger">*</span></label>
                      <input type="text" class="form-control form-control-sm" v-model="form.lastName" placeholder="Last name" />
                    </div>
                  </div>
                  <div class="mb-3">
                    <label class="form-label text-xs">Nickname</label>
                    <input type="text" class="form-control form-control-sm" v-model="form.nickname" placeholder="Display name (optional)" />
                  </div>
                  <div class="mb-3">
                    <label class="form-label text-xs">Email <span class="text-danger">*</span></label>
                    <input type="email" class="form-control form-control-sm" v-model="form.email" placeholder="email@example.com" />
                  </div>
                  <div v-if="!editId" class="mb-3">
                    <label class="form-label text-xs">Password <span class="text-danger">*</span></label>
                    <input type="password" class="form-control form-control-sm" v-model="form.password" placeholder="Password" />
                  </div>
                  <div v-if="editId && !isPendingEdit" class="mb-3">
                    <label class="form-label text-xs d-block">Password</label>
                    <button type="button" class="btn btn-sm btn-outline-warning" :disabled="resetLoading" @click="requestPasswordReset">
                      <span v-if="resetLoading"><i class="fas fa-spinner fa-spin me-1"></i></span>
                      Reset password (user sets new password after next login)
                    </button>
                    <p class="text-secondary text-xs mb-0 mt-1">User signs in with their current password, then must choose a new password.</p>
                  </div>
                  <div class="mb-3">
                    <label class="form-label text-xs d-block mb-2">Roles <span class="text-danger">*</span></label>
                    <div v-if="roleOptions.length === 0" class="text-secondary text-xs">
                      No roles available.
                    </div>
                    <div v-else class="picker-access-list picker-access-list--roles border rounded">
                      <div
                        v-for="opt in roleOptions"
                        :key="opt.id"
                        class="picker-access-row"
                      >
                        <label class="bo-checkbox mb-0 picker-access-check">
                          <input
                            class="bo-checkbox-input"
                            type="checkbox"
                            :value="Number(opt.id)"
                            v-model="form.roleIds"
                          />
                          <span class="bo-checkbox-box"></span>
                        </label>
                        <button
                          type="button"
                          class="picker-access-name"
                          @click="toggleRole(opt.id)"
                        >
                          {{ opt.label }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="mb-3">
                    <label class="form-label text-xs d-block mb-2">Team <span class="text-danger">*</span></label>
                    <div class="picker-access-list border rounded">
                      <div
                        v-for="opt in teamOptions"
                        :key="opt.value"
                        class="picker-access-row"
                      >
                        <label class="bo-checkbox mb-0 picker-access-check">
                          <input
                            class="bo-checkbox-input"
                            type="checkbox"
                            :checked="form.teams.includes(opt.value)"
                            @change="toggleTeam(opt.value)"
                          />
                          <span class="bo-checkbox-box"></span>
                        </label>
                        <button
                          type="button"
                          class="picker-access-name"
                          @click="toggleTeam(opt.value)"
                        >
                          {{ opt.label }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-if="showSubProjectAccess" class="mb-3 user-modal-projects">
                    <label class="form-label text-xs d-block mb-2">Projects access</label>
                    <p class="text-secondary text-xs mb-3">
                      For Outsource / Vendor — select which sub projects this user can view. Leave empty for no access.
                    </p>
                    <div v-if="subProjectOptions.length === 0" class="text-secondary text-xs">
                      No active sub projects available.
                    </div>
                    <div v-else class="picker-access-list picker-access-list--projects border rounded">
                      <div
                        v-for="sp in subProjectOptions"
                        :key="sp.subProjectId"
                        class="picker-access-row"
                      >
                        <label class="bo-checkbox mb-0 picker-access-check">
                          <input
                            class="bo-checkbox-input"
                            type="checkbox"
                            :value="Number(sp.subProjectId)"
                            v-model="form.subProjectIds"
                          />
                          <span class="bo-checkbox-box"></span>
                        </label>
                        <button
                          type="button"
                          class="picker-access-name"
                          @click="toggleSubProjectAccess(sp.subProjectId)"
                        >
                          {{ sp.projectName ? `${sp.projectName} / ` : '' }}{{ sp.subProjectName }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="modal-footer flex-shrink-0 d-flex align-items-center justify-content-end gap-2">
                  <button type="button" class="btn btn-sm btn-secondary mb-0" @click="closeModal">Cancel</button>
                  <button
                    v-if="isPendingEdit"
                    type="button"
                    class="btn btn-sm btn-outline-danger mb-0"
                    :disabled="rejectLoading"
                    @click="rejectPending"
                  >
                    <span v-if="rejectLoading"><i class="fas fa-spinner fa-spin me-1"></i></span>
                    Reject
                  </button>
                  <button type="button" class="btn btn-sm btn-dark mb-0" :disabled="saveLoading" @click="save">
                    <span v-if="saveLoading"><i class="fas fa-spinner fa-spin me-1"></i></span>
                    {{ isPendingEdit ? 'Approve' : 'Save' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="card-header pb-0">
            <div class="tab-shell" role="tablist" aria-label="User setting views">
              <div
                class="tab-item"
                :class="{ active: activeTab === 'users' }"
                role="tab"
                :aria-selected="activeTab === 'users'"
                @click="switchTab('users')"
              >
                USER
              </div>
              <div
                class="tab-item"
                :class="{ active: activeTab === 'requests' }"
                role="tab"
                :aria-selected="activeTab === 'requests'"
                @click="switchTab('requests')"
              >
                REQUEST REGISTER
                <span v-if="pendingUsers.length" class="tab-badge">{{ pendingUsers.length }}</span>
              </div>
            </div>
          </div>

          <div class="user-shell-content">
          <!-- Tab: approved users -->
          <template v-if="activeTab === 'users'">
            <div class="d-flex justify-content-end mb-3 px-2">
              <button class="btn btn-sm btn-outline-secondary mb-0" style="font-size:0.8rem;" @click="openCreate">
                <i class="fas fa-plus me-1"></i> Create User
              </button>
            </div>
            <div class="px-2 pb-2">
                <div v-if="loadingUsers" class="text-center py-5">
                  <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
                  <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
                </div>
                <div v-else class="user-table">
                  <div class="user-table-header">
                    <span class="col-name">NAME</span>
                    <span class="col-email">EMAIL</span>
                    <span class="col-role text-center">ROLES</span>
                    <span class="col-team text-center">TEAM</span>
                    <span class="col-action text-center">ACTIONS</span>
                  </div>
                  <div v-if="users.length === 0" class="pm-empty-table-row">
                    <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                    <p class="text-secondary text-sm mt-2 mb-0">No users yet. Click "Create User" to add one.</p>
                  </div>
                  <div v-for="u in users" :key="u.userId" class="user-row">
                    <div class="col-name">
                      <p class="user-name mb-0">{{ formatListName(u) }}</p>
                    </div>
                    <div class="col-email">
                      <span class="text-xs text-secondary">{{ u.username || '—' }}</span>
                    </div>
                    <div class="col-role text-center roles-cell">
                      <template v-if="displayUserRoles(u).length">
                        <span
                          v-for="role in displayUserRoles(u)"
                          :key="role.roleId"
                          class="role-badge me-1 mb-1"
                          :style="roleStyle(role.roleId, role.roleName)"
                        >{{ (role.roleName || roleLabel(role.roleId)).toUpperCase() }}</span>
                      </template>
                      <span v-else class="text-xs text-secondary">—</span>
                    </div>
                    <div class="col-team text-center">
                      <span class="text-xs text-secondary">{{ formatTeam(u.team) }}</span>
                    </div>
                    <div class="col-action text-center">
                      <button class="btn btn-link btn-sm p-0 action-edit me-2" title="Edit" @click="openEdit(u)">
                        <i class="fas fa-edit" style="font-size:0.85rem;"></i>
                      </button>
                      <button class="btn btn-link btn-sm p-0 action-delete" title="Delete" @click="remove(u)">
                        <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </template>

          <!-- Tab: pending registration requests -->
          <template v-else>
            <div class="px-2 pb-2">
                <p class="text-secondary text-xs mb-3">
                  Users who completed Google sign-up and are waiting for admin approval.
                </p>
                <div v-if="loadingPending" class="text-center py-5">
                  <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
                  <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
                </div>
                <div v-else class="user-table">
                  <div class="user-table-header">
                    <span class="col-name">NAME</span>
                    <span class="col-email">EMAIL</span>
                    <span class="col-team text-center">TEAM</span>
                    <span class="col-date text-center">REQUESTED</span>
                    <span class="col-action text-center">ACTIONS</span>
                  </div>
                  <div v-if="pendingUsers.length === 0" class="pm-empty-table-row">
                    <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                    <p class="text-secondary text-sm mt-2 mb-0">No pending registration requests.</p>
                  </div>
                  <div v-for="u in pendingUsers" :key="u.userId" class="user-row">
                    <div class="col-name">
                      <p class="user-name mb-0">{{ formatListName(u) }}</p>
                    </div>
                    <div class="col-email">
                      <span class="text-xs text-secondary">{{ u.username || '—' }}</span>
                    </div>
                    <div class="col-team text-center">
                      <span class="text-xs text-secondary">{{ formatTeam(u.team) }}</span>
                    </div>
                    <div class="col-date text-center">
                      <span class="text-xs text-secondary">{{ formatDate(u.createdAt) }}</span>
                    </div>
                    <div class="col-action text-center">
                      <button class="btn btn-link btn-sm p-0 action-edit" title="Edit" @click="openEdit(u)">
                        <i class="fas fa-edit" style="font-size:0.85rem;"></i>
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usersApi, rolesApi, subProjectsApi } from "@/services/backendApi";
import { roleBadgeStyle } from "@/utils/roleColors";
import {
  TEAM_OPTIONS,
  formatUserTeams,
  parseUserTeams,
  serializeUserTeams,
  toggleTeamSelection,
} from "@/utils/userTeams";

const SCOPED_ROLE_NAMES = new Set(["outsource", "vendor"]);
const EXCLUDED_SUB_PROJECT_STATUSES = new Set(["deployed", "hold", "cancel"]);

export default {
  name: "user-setting",
  data() {
    return {
      activeTab: "users",
      loadingUsers: false,
      loadingPending: false,
      showModal: false,
      editId: null,
      resetLoading: false,
      saveLoading: false,
      rejectLoading: false,
      isPendingEdit: false,
      form: {
        firstName: "",
        lastName: "",
        nickname: "",
        email: "",
        password: "",
        roleIds: [],
        teams: [],
        subProjectIds: [],
      },
      users: [],
      pendingUsers: [],
      roleOptions: [],
      allSubProjects: [],
      pendingLoaded: false,
      lockedModalHeight: null,
    };
  },
  watch: {
    formHasScopedRole: {
      handler(val, oldVal) {
        if (val && !oldVal) {
          this.lockModalHeightFromCurrent();
        } else if (!val && oldVal) {
          this.lockedModalHeight = null;
        }
      },
      flush: "pre",
    },
  },
  async created() {
    await Promise.all([this.loadRoles(), this.loadUsers(), this.loadSubProjects()]);
  },
  computed: {
    modalTitle() {
      if (!this.editId) return "Create User";
      if (this.isPendingEdit) return "Review Registration";
      return "Edit User";
    },
    showSubProjectAccess() {
      return this.formHasScopedRole;
    },
    formHasScopedRole() {
      const selected = new Set((this.form.roleIds || []).map((id) => Number(id)));
      return (this.roleOptions || []).some(
        (opt) =>
          selected.has(Number(opt.id)) &&
          SCOPED_ROLE_NAMES.has(String(opt.label || "").trim().toLowerCase())
      );
    },
    subProjectOptions() {
      const list = (Array.isArray(this.allSubProjects) ? this.allSubProjects : []).filter((sp) => {
        const status = String(sp.subProjectStatus || "").trim().toLowerCase();
        return !EXCLUDED_SUB_PROJECT_STATUSES.has(status);
      });
      return [...list].sort((a, b) => {
        const pa = String(a.projectName || "");
        const pb = String(b.projectName || "");
        if (pa !== pb) return pa.localeCompare(pb);
        return String(a.subProjectName || "").localeCompare(String(b.subProjectName || ""));
      });
    },
    teamOptions() {
      return TEAM_OPTIONS;
    },
    userModalContentStyle() {
      if (!this.lockedModalHeight) return {};
      return { height: `${this.lockedModalHeight}px` };
    },
  },
  methods: {
    lockModalHeightFromCurrent() {
      const el = this.$refs.userModalContent;
      if (el && el.offsetHeight > 0) {
        this.lockedModalHeight = el.offsetHeight;
      }
    },

    scheduleModalHeightLock() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if (!this.showModal || !this.showSubProjectAccess) return;
          if (this.lockedModalHeight) return;
          const el = this.$refs.userModalContent;
          if (!el) return;
          const projects = el.querySelector(".user-modal-projects");
          const projectsH = projects ? projects.offsetHeight : 0;
          const total = el.offsetHeight;
          if (total > projectsH) {
            this.lockedModalHeight = Math.max(300, total - projectsH);
          } else {
            this.lockModalHeightFromCurrent();
          }
        });
      });
    },

    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "requests" && !this.pendingLoaded) {
        this.loadPendingUsers();
      }
    },

    async loadRoles() {
      try {
        const rows = await rolesApi.getAll();
        this.roleOptions = (rows || []).map((r) => ({
          id: Number(r.roleId),
          label: r.roleName || `Role ${r.roleId}`,
        }));
      } catch (e) {
        console.error("loadRoles error:", e);
        try {
          const us = await usersApi.getAll();
          const map = new Map();
          (us || []).forEach((u) => {
            (u.roles || []).forEach((r) => {
              if (!map.has(r.roleId)) map.set(r.roleId, r.roleName || `Role ${r.roleId}`);
            });
            if (u.roleId && !map.has(u.roleId)) map.set(u.roleId, u.roleName || `Role ${u.roleId}`);
          });
          this.roleOptions = [...map.entries()]
            .map(([id, label]) => ({ id: Number(id), label }))
            .sort((a, b) => a.id - b.id);
        } catch (e2) {
          console.error("fallback loadRoles from users error:", e2);
          this.roleOptions = [];
        }
      }
    },

    formatListName(u) {
      const fn = (u.firstName || "").trim();
      const ln = (u.lastName || "").trim();
      const nn = (u.nickname || "").trim();
      if (nn) return `${fn} (${nn}) ${ln}`.trim();
      return `${fn} ${ln}`.trim() || "—";
    },

    formatTeam(team) {
      return formatUserTeams(team);
    },

    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    displayUserRoles(u) {
      if (u.roles && u.roles.length) return u.roles;
      if (u.roleId) return [{ roleId: u.roleId, roleName: u.roleName }];
      return [];
    },

    toggleTeam(value) {
      this.form.teams = toggleTeamSelection(this.form.teams, value);
    },

    toggleRole(roleId) {
      const id = Number(roleId);
      if (!id) return;
      const ids = [...(this.form.roleIds || [])].map((v) => Number(v));
      const idx = ids.indexOf(id);
      if (idx === -1) ids.push(id);
      else ids.splice(idx, 1);
      this.form.roleIds = ids;
    },

    toggleSubProjectAccess(subProjectId) {
      const id = Number(subProjectId);
      if (!id) return;
      const ids = [...(this.form.subProjectIds || [])].map((v) => Number(v));
      const idx = ids.indexOf(id);
      if (idx === -1) ids.push(id);
      else ids.splice(idx, 1);
      this.form.subProjectIds = ids;
    },

    async loadSubProjects() {
      try {
        this.allSubProjects = await subProjectsApi.getAll({ excludeDeployed: "true" });
      } catch (e) {
        console.error("loadSubProjects error:", e);
        this.allSubProjects = [];
      }
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        this.users = await usersApi.getAll();
      } catch (e) {
        console.error("loadUsers error:", e);
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadPendingUsers() {
      this.loadingPending = true;
      try {
        this.pendingUsers = await usersApi.getPendingRegistrations();
        this.pendingLoaded = true;
      } catch (e) {
        console.error("loadPendingUsers error:", e);
        alert(e.message || "Failed to load registration requests");
      } finally {
        this.loadingPending = false;
      }
    },

    openCreate() {
      this.editId = null;
      this.isPendingEdit = false;
      this.form = {
        firstName: "",
        lastName: "",
        nickname: "",
        email: "",
        password: "",
        roleIds: [],
        teams: [],
        subProjectIds: [],
      };
      this.lockedModalHeight = null;
      this.showModal = true;
      this.scheduleModalHeightLock();
    },

    openEdit(u) {
      this.editId = u.userId;
      this.isPendingEdit = this.pendingUsers.some((p) => Number(p.userId) === Number(u.userId));
      const ids = u.roles && u.roles.length ? u.roles.map((r) => r.roleId) : u.roleId ? [u.roleId] : [];
      const subProjectIds = (u.subProjectIds || []).map((id) => Number(id)).filter((n) => n > 0);
      const teams = u.teams?.length ? [...u.teams] : parseUserTeams(u.team);
      this.form = {
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        nickname: u.nickname || "",
        email: u.username || "",
        password: "",
        roleIds: [...ids],
        teams,
        subProjectIds: [...subProjectIds],
      };
      this.lockedModalHeight = null;
      this.showModal = true;
      this.scheduleModalHeightLock();
    },

    closeModal() {
      this.showModal = false;
      this.isPendingEdit = false;
      this.lockedModalHeight = null;
    },

    roleLabel(roleId) {
      const hit = this.roleOptions.find((r) => Number(r.id) === Number(roleId));
      return hit?.label || "—";
    },

    roleStyle(roleId, roleName) {
      return roleBadgeStyle(roleId, roleName);
    },

    async requestPasswordReset() {
      if (!this.editId) return;
      if (!confirm("This user must set a new password after they sign in with their current password. Continue?")) return;
      this.resetLoading = true;
      try {
        await usersApi.requestPasswordReset(this.editId);
        alert("Password reset scheduled. The user will be prompted to set a new password after their next login.");
      } catch (e) {
        console.error("reset password error:", e);
        alert(e.message || "Request failed");
      } finally {
        this.resetLoading = false;
      }
    },

    async save() {
      if (!this.form.firstName.trim() || !this.form.lastName.trim() || !this.form.email.trim()) return;
      if (!this.form.teams || this.form.teams.length === 0) return;
      if (!this.form.roleIds || this.form.roleIds.length === 0) return;
      if (!this.editId && (!this.form.password || this.form.password.length < 6)) return;
      this.saveLoading = true;
      try {
        const payload = {
          firstName: this.form.firstName.trim(),
          lastName: this.form.lastName.trim(),
          nickname: this.form.nickname.trim(),
          email: this.form.email.trim(),
          team: serializeUserTeams(this.form.teams),
          teams: [...this.form.teams],
          roleIds: [...this.form.roleIds],
          subProjectIds: this.showSubProjectAccess
            ? [...(this.form.subProjectIds || [])].map((id) => Number(id))
            : [],
        };
        if (!this.editId) payload.password = this.form.password;
        if (this.editId) {
          await usersApi.update(this.editId, payload);
          if (this.isPendingEdit) {
            await usersApi.approve(this.editId);
            await this.loadPendingUsers();
          }
        } else {
          await usersApi.insert(payload);
        }
        await this.loadUsers();
        this.closeModal();
      } catch (e) {
        console.error("save user error:", e);
        alert(e.message || "Save failed");
      } finally {
        this.saveLoading = false;
      }
    },

    async rejectPending() {
      if (!this.editId || !this.isPendingEdit) return;
      const name = `${this.form.firstName} ${this.form.lastName}`.trim() || this.form.email;
      if (!confirm(`Reject registration for "${name}"? This will remove the request.`)) return;
      this.rejectLoading = true;
      try {
        await usersApi.delete(this.editId);
        await this.loadPendingUsers();
        this.closeModal();
      } catch (e) {
        console.error("reject registration error:", e);
        alert(e.message || "Reject failed");
      } finally {
        this.rejectLoading = false;
      }
    },

    async remove(u) {
      if (!confirm(`Delete user "${this.formatListName(u)}"?`)) return;
      try {
        await usersApi.delete(u.userId);
        await this.loadUsers();
      } catch (e) {
        console.error("delete user error:", e);
        alert(e.message || "Delete failed");
      }
    },

  },
};
</script>

<style scoped>
.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}

.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  padding: 0.05rem 0.35rem;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1.2;
  background: rgba(255, 255, 255, 0.25);
  color: inherit;
}

.tab-item:not(.active) .tab-badge {
  background: #fff3cd;
  color: #856404;
}

.user-shell-card {
  border-radius: 0.75rem;
}

.user-shell-card .card-header {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.user-shell-content {
  padding-top: 1rem;
}

.user-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 280px;
}

.user-table .pm-empty-table-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  width: 100%;
  min-height: 180px;
  padding: 2.5rem 1rem;
  text-align: center;
  box-sizing: border-box;
}

.user-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.user-row {
  display: flex;
  align-items: center;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}

.user-row:last-child {
  border-bottom: none;
}

.user-row:hover {
  background: #fafbfc;
}

.col-name {
  flex: 2.2;
  min-width: 0;
}

.col-email {
  flex: 2.2;
  min-width: 0;
}

.col-role {
  flex: 1.6;
  min-width: 90px;
}

.col-team {
  flex: 0.9;
  min-width: 72px;
}

.col-date {
  flex: 1.4;
  min-width: 120px;
}

.col-action {
  flex: 0.8;
  min-width: 72px;
}

.roles-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
}

.role-badge {
  display: inline-block;
  padding: 0.25rem 0.65rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 700;
}

.action-edit {
  color: #4361ee;
}

.action-edit:hover {
  color: #2d4bc8;
}

.action-delete {
  color: #e74c3c;
}

.action-delete:hover {
  color: #c0392b;
}

/* Override Bootstrap centered dialog min-height (prevents popup growing with content). */
.user-modal .user-modal-dialog.modal-dialog-centered {
  min-height: 0;
  max-height: calc(100vh - 2rem);
  max-width: 560px;
  width: calc(100% - 2rem);
  margin: 1rem auto;
  display: flex;
  align-items: center;
}

.user-modal .user-modal-content {
  width: 100%;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
}

.user-modal .user-modal-content.user-modal-content--locked {
  overflow: hidden;
}

.user-modal .user-modal-body.user-modal-body--locked {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

.picker-access-list {
  padding: 0.5rem 0.65rem;
}

.picker-access-list--roles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 0.65rem;
  row-gap: 0.1rem;
}

.picker-access-list--projects {
  max-height: 160px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.picker-access-list--roles .picker-access-row {
  border-bottom: none;
  padding: 0.4rem 0.2rem;
}

.picker-access-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0.35rem;
  border-bottom: 1px solid #f1f3f5;
}

.picker-access-list--projects .picker-access-row:last-child {
  border-bottom: none;
}

.picker-access-check {
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.picker-access-name {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #344767;
  cursor: pointer;
}

.picker-access-name:hover {
  color: #4361ee;
}
</style>
